import {
    Alert,
    AlertContent,
    Button,
    Group,
    Input,
    Label,
    Modal,
    ModalCloseButton,
    ModalTitle,
    Option,
    Select,
} from '@tlx/atlas';

import { useTskContext } from '@Hooks/useTskContext';
import { EmployeeInfo } from '@Page/EmployeeOverview/modals/taxcard/TaxcardModal';
import { GRONN_TIGER, PREBEN_COMPANYID } from '@Page/SalaryTaxCard/SalaryTaxCardPage';
import { InputError } from '@Page/SalaryTaxCard/component/InputError/InputError';
import { PasswordInput } from '@Page/SalaryTaxCard/component/PasswordInput/PasswordInput';
import { AltinnData, Success } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import { APIError, fetcher, InlineMarkdown, ListResponse } from '@tlx/astro-shared';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getValidationMessageDigitsOnly } from '@Page/SalaryTaxCard/validations.ts';

interface AltinnLoginFormProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    altinn: AltinnData;
    employeeInfo?: EmployeeInfo;
}

type ReportingCompany = {
    id: number;
    displayName: string;
};

export function AltinnLoginForm({ open, setOpen, altinn, employeeInfo }: AltinnLoginFormProps) {
    const tsk = useTskContext();
    const intl = useIntl();

    const [disableProceedButton, setDisableProceedButton] = useState(true);
    const [disableOtpButton, setDisableOtpButton] = useState(!!altinn.hasRequestedPin);
    const [reportingCompanyId, setReportingCompanyId] = useState(tsk.contextCompanyId);

    const [reportingCompanies, setReportingCompanies] = useState<ReportingCompany[]>([]);

    const [ssn, setSSN] = useState(employeeInfo?.nationalIdentityNumber ?? '');
    const [pwd, setPwd] = useState('');
    const [otp, setOtp] = useState('');

    const [errorSSN, setErrorSSN] = useState('');

    useEffect(() => {
        setSSN(employeeInfo?.nationalIdentityNumber ?? '');
    }, [employeeInfo]);

    useEffect(() => {
        async function fetchDefaultReportingCompany() {
            return fetcher<ListResponse<ReportingCompany>>('/v2/salary/tskinternal/reportingCompany');
        }

        fetchDefaultReportingCompany().then((res) => {
            setReportingCompanyId(res.values.length > 0 ? res.values[res.values.length - 1].id : 0);
            setReportingCompanies(res.values);
        });
    }, []);

    useEffect(() => {
        if (!getValidationMessageDigitsOnly(ssn)) {
            return;
        }
        if (altinn.hasRequestedPin) {
            setDisableOtpButton(false);
        } else if (ssn?.length >= 11 && pwd.trim().length > 0) {
            setDisableOtpButton(false);
            if (otp) {
                setDisableProceedButton(false);
            } else {
                setDisableProceedButton(true);
            }
        } else {
            setDisableOtpButton(true);
            setDisableProceedButton(true);
        }
        //reportingCompanyId is in the dependency because it should run again
    }, [altinn.hasRequestedPin, ssn, pwd, otp, reportingCompanyId]);

    useEffect(() => {
        const validationMessageKey = getValidationMessageDigitsOnly(ssn);
        if (validationMessageKey) {
            setErrorSSN(intl.formatMessage({ id: validationMessageKey }));
        }
    }, [ssn]);

    useEffect(() => {
        if (altinn.hasRequestedLogin) {
            setDisableProceedButton(false);
        } else {
            setDisableProceedButton(true);
        }
    }, [altinn.hasRequestedLogin]);

    return (
        <Modal open={open} onClose={() => setOpen(false)} className="modal-w-altinn-login-form">
            <ModalCloseButton />
            <ModalTitle>
                <FormattedMessage id={'text_altinn_login'} />
            </ModalTitle>
            {tsk.isProxyLogin && reportingCompanies.length > 0 && (
                <Group className="atl-py-8">
                    <Label htmlFor="altinnID">
                        <FormattedMessage id={'text_use_altinn_id'} />
                    </Label>
                    <Select
                        className="atl-w-full trx-text-overflow-ellipsis"
                        data-testid={'SelectReportingCompany'}
                        value={reportingCompanyId.toString()}
                        onChange={(event) => {
                            setReportingCompanyId(parseInt(event.target.value));
                        }}
                    >
                        {reportingCompanies.map((reportingCompany) => (
                            <Option key={reportingCompany.id} value={reportingCompany.id.toString()}>
                                {reportingCompany.displayName}
                            </Option>
                        ))}
                    </Select>
                </Group>
            )}

            <Group className="atl-py-8">
                <Label htmlFor="altinnLoginSSN">
                    <FormattedMessage id={'text_social_security_number'} />
                </Label>
                <InputError errors={errorSSN}>
                    <Input
                        data-testid="national_identity_number"
                        key="altinnLoginSSN"
                        id="altinnLoginSSN"
                        type="text"
                        required
                        value={ssn}
                        autoComplete="off"
                        maxLength={11}
                        onChange={(event) => {
                            setSSN(event.target.value);
                            removeValidation(altinn);
                        }}
                    />
                </InputError>
            </Group>

            <PasswordInput
                id="altinnLoginPwd"
                label={intl.formatMessage({
                    id: 'text_personal_altinn_password',
                })}
                pwd={pwd}
                setPwd={setPwd}
            />

            <div className="atl-flex atl-flex-row">
                <PasswordInput
                    id="altinnLoginOtp"
                    label={intl.formatMessage({ id: 'text_pin' })}
                    pwd={otp}
                    setPwd={setOtp}
                    small
                />
                <Button
                    style={{ marginLeft: '40px', marginTop: '36px' }}
                    data-testid="otp-sendnew"
                    variant="tertiary"
                    onClick={() => {
                        altinn.dispatch?.({
                            type: 'LOGIN_TO_ALTINN_REQUEST_PIN',
                            reportingCompanyId: reportingCompanyId,
                            personalIdNumber: ssn,
                            personalAltinnPassword: pwd,
                        });
                    }}
                    disabled={disableOtpButton}
                >
                    <FormattedMessage id={'text_send_new_otp_altinn'} />
                </Button>
            </div>
            {altinn.error ? (
                <Alert variant="error" className="atl-mt-8">
                    <AlertContent>{textFromValidations(altinn.error)}</AlertContent>
                </Alert>
            ) : (
                <></>
            )}
            {altinn.success.length > 0 ? (
                <Alert variant="success" className="atl-mt-8">
                    <AlertContent>{textFromSuccess(altinn.success, 'LOGIN_TO_ALTINN_REQUEST_PIN')}</AlertContent>
                </Alert>
            ) : (
                <></>
            )}

            <div className="atl-flex atl-mt-16">
                <Button
                    className="atl-button--primary atl-mr-8"
                    disabled={disableProceedButton}
                    onClick={() => {
                        altinn.dispatch?.({
                            type: 'LOGIN_TO_ALTINN_VERIFY_PIN',
                            reportingCompanyId: reportingCompanyId,
                            personalIdNumber: ssn,
                            personalAltinnPassword: pwd,
                            pin: otp,
                        });
                    }}
                    data-testid="resume-button"
                >
                    <FormattedMessage id={'text_altinn_login'} />
                </Button>
                <Button
                    className="atl-button--tertiary"
                    onClick={() => {
                        setOpen(false);
                    }}
                    data-testid="cancel-button"
                >
                    <FormattedMessage id={'button_cancel'} />
                </Button>
                {(tsk.contextCompanyId === PREBEN_COMPANYID || tsk.contextCompanyId === GRONN_TIGER) && (
                    <Button
                        className="atl-button--tertiary"
                        onClick={() => {
                            altinn.dispatch?.({ type: 'LOGIN_TO_TEST_ALTINN' });
                        }}
                        data-testid={'test-altinn-login'}
                    >
                        TT02
                    </Button>
                )}
            </div>
        </Modal>
    );
}

function textFromValidations(err: Error | undefined) {
    if (err instanceof APIError) {
        if (err.details.validationMessages) {
            return (
                <div style={{ color: 'red' }}>
                    {err.details.validationMessages.map((x, index) => (
                        <InlineMarkdown key={index}>{x.message}</InlineMarkdown>
                    ))}
                </div>
            );
        } else {
            return <></>;
        }
    } else {
        return <></>;
    }
}

function textFromSuccess(successes: Success[], area: string) {
    const filteredMessages: Success[] = successes.filter((success) => (success.area = area));
    return (
        <div style={{ color: 'green' }}>
            {filteredMessages.map((success: Success, index) => (
                <div key={index}>
                    {success.message.map((message, index) => (
                        <div key={index}>{message}</div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function removeValidation(altinn: AltinnData) {
    if (altinn.error) {
        altinn.dispatch?.({
            type: 'INIT',
        });
    }
}
